import type { EmailTemplate } from '@common/types/lawfirms';
import type { ProspectResponse } from '@common/types/prospects';
import { Box, Button, Divider, Grid, Modal, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import LexoFooterLogo from '@/assets/powered_by_lexo.svg';
import LoadingButton from '@/components/Buttons/LoadingButton';
import { THIN_SCROLLBAR_STYLING } from '@/constants';
import { quoteTemplateQueryOptions } from '@/queries/templates';
import axiosInstance from '@/services/axiosInstance';
import { sendQuoteManually } from '@/services/ProspectService';
import { convertToHtml } from '@/utils';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';
import { mapEmailTemplateToQuoteEmailData } from '../prospectDetailsState';
import SendQuoteManuallyQuoteStructure from './SendQuoteManuallyQuoteStructure';

interface Props {
    prospectId?: string;
    onClose: () => void;
    prospect: ProspectResponse;
}

export default function SendManualQuoteModal({ prospectId, onClose, prospect }: Props) {
    const theme = useTheme();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { data: quoteEmailData } = useQuery({
        queryKey: ['quote-email-template', prospect.quoteTemplateId],
        queryFn: async () => {
            const response = await axiosInstance.get<EmailTemplate>(
                `/api/v1/quote-templates/${prospect.quoteTemplateId}/quote-email-template`,
            );
            return mapEmailTemplateToQuoteEmailData(response.data);
        },
        enabled: !!prospect.quoteTemplateId,
    });

    const { data: quoteResp } = useQuery(quoteTemplateQueryOptions(prospect.quoteTemplateId));

    //handle send quote manually API integration
    const handleSendQuoteManually = async () => {
        if (prospectId) {
            setIsLoading(true);
            try {
                await sendQuoteManually(prospectId);
                successSnackbar('Quote has been successfully sent.');
                onClose();
            } catch (error: any) {
                console.error(error);
                errorSnackbar(error?.response?.data || 'Something went wrong!');
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <Modal open onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    maxWidth: '800px',
                    width: '100%',
                    bgcolor: theme.palette.neutral.white,
                    borderRadius: '8px',
                }}
            >
                <Box sx={{ padding: '24px 24px 16px 24px' }}>
                    <Grid container sx={{ gap: '16px' }} spacing={0}>
                        <Grid item xs={12}>
                            <Typography variant="h2">Send Quote?</Typography>
                            <Typography variant="body2" sx={{ marginTop: '8px' }}>
                                Do you want to send a quote email to {prospect.contact?.contactName}
                                ? You can preview your quote request response below.
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sx={{
                                maxHeight: '70vh',
                                overflow: 'auto',
                                ...THIN_SCROLLBAR_STYLING,
                            }}
                        >
                            <Box sx={{ border: `1px solid ${theme.palette.neutral.borderColor}` }}>
                                <Box
                                    sx={{
                                        backgroundColor: theme.palette.neutral.N_50,
                                        padding: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '24px',
                                    }}
                                >
                                    {prospect.lawFirm?.logoUrl && (
                                        <img
                                            alt="Law firm logo"
                                            src={`${import.meta.env.VITE_APP_API_URL}/api/v1/files/${
                                                prospect.lawFirm.logoUrl
                                            }`}
                                            style={{ width: '80px', height: '80px' }}
                                        ></img>
                                    )}
                                    <Typography variant="h3">
                                        {prospect.lawFirm?.companyName}
                                    </Typography>
                                </Box>
                                <Box sx={{ marginTop: '16px', padding: '8px 16px' }}>
                                    <Typography variant="h2">Quote request response</Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            marginTop: '24px',
                                            color: 'text.primary',
                                        }}
                                    >
                                        Hi {prospect.contact?.firstName}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            marginTop: '24px',
                                            color: 'text.primary',
                                        }}
                                    >
                                        {quoteEmailData?.emailBodyIntroduction}
                                    </Typography>
                                    <SendQuoteManuallyQuoteStructure
                                        prospect={prospect}
                                        quoteResp={quoteResp}
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            marginTop: '24px',
                                            color: 'text.primary',
                                        }}
                                    >
                                        {quoteEmailData?.emailBodyClosing}
                                    </Typography>
                                    <Typography variant="h3" sx={{ marginTop: '24px' }}>
                                        Select one of the options below to respond
                                    </Typography>
                                    <Box
                                        sx={{
                                            marginTop: '16px',
                                            display: 'flex',
                                            justifyContent: 'flex-start',
                                            gap: '8px',
                                        }}
                                    >
                                        <Button variant="contained">Accept</Button>
                                        <Button variant="outlined">Request a call</Button>
                                        <Button variant="outlined">Decline</Button>
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: '12px',
                                            color: '#555',
                                            fontStyle: 'italic',
                                            lineHeight: 1.5,
                                            mt: 1.5,
                                            paddingTop: '10px',
                                            whiteSpace: 'pre-wrap',
                                        }}
                                    >
                                        {quoteEmailData?.disclaimerText && (
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: convertToHtml(
                                                        quoteEmailData?.disclaimerText,
                                                    ),
                                                }}
                                            ></div>
                                        )}
                                    </Typography>
                                    <Typography
                                        dangerouslySetInnerHTML={{
                                            __html: convertToHtml(
                                                quoteEmailData?.emailSignature || '',
                                            ),
                                        }}
                                        sx={{
                                            display: 'block',
                                            marginTop: '24px',
                                            color: 'text.primary',
                                        }}
                                        variant="body1"
                                    ></Typography>
                                    <img
                                        alt="Lexo logo"
                                        src={LexoFooterLogo}
                                        style={{ marginTop: '24px' }}
                                    ></img>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
                <Divider />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        padding: '16px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                        }}
                    >
                        <Button variant="outlined" onClick={onClose}>
                            Cancel
                        </Button>
                        <LoadingButton
                            variant="contained"
                            loading={isLoading}
                            onClick={handleSendQuoteManually}
                        >
                            Confirm
                        </LoadingButton>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
}
